import React, { useState, useEffect } from 'react'
import { Card, Button, Table, DatePicker, Select, Statistic, Row, Col, Modal, message } from 'antd'
import { DownloadOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { RangePicker } = DatePicker

const DataExport = () => {
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState(null)
  const [exportType, setExportType] = useState('parents')
  const [dateRange, setDateRange] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/export/stats`)
      const data = await res.json()
      if (data.success) {
        setStats(data.data)
      }
    } catch (err) {
      console.error('加载统计失败:', err)
    }
  }

  // 执行导出
  const handleExport = async () => {
    setLoading(true)
    
    try {
      const params = new URLSearchParams()
      params.append('type', exportType)
      params.append('format', 'csv')
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD'))
        params.append('endDate', dateRange[1].format('YYYY-MM-DD'))
      }

      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE}/admin/export?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      // 获取文件名
      const disposition = response.headers.get('Content-Disposition')
      let filename = `${exportType}_export.csv`
      if (disposition) {
        const match = disposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;\n"']+)/i)
        if (match) {
          filename = decodeURIComponent(match[1])
        }
      }

      // 下载文件
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      message.success('导出成功')

    } catch (err) {
      message.error('导出失败: ' + err.message)
    }

    setLoading(false)
  }

  const exportTypes = [
    { id: 'parents', name: '家长用户', icon: '👨‍👩‍👧', count: stats?.parents || 0 },
    { id: 'children', name: '孩子档案', icon: '👶', count: stats?.children || 0 },
    { id: 'orders', name: '订单数据', icon: '📦', count: stats?.orders || 0 },
    { id: 'products', name: '产品数据', icon: '📚', count: stats?.products || 0 },
    { id: 'learning', name: '学习记录', icon: '📝', count: stats?.learnings || 0 },
    { id: 'activities', name: '活动数据', icon: '🎪', count: stats?.activities || 0 },
    { id: 'notifications', name: '通知记录', icon: '🔔', count: stats?.notifications || 0 }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>📤 数据导出</h2>

      {/* 统计概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="家长用户" value={stats?.parents || 0} prefix="👨‍👩‍👧" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="孩子档案" value={stats?.children || 0} prefix="👶" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="订单数据" value={stats?.orders || 0} prefix="📦" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="本月新增家长" value={stats?.monthlyParents || 0} prefix="📈" />
          </Card>
        </Col>
      </Row>

      {/* 导出选项 */}
      <Card title="选择导出数据">
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {exportTypes.map(type => (
            <Col span={6} key={type.id} style={{ marginBottom: 16 }}>
              <div
                onClick={() => setExportType(type.id)}
                style={{
                  padding: 16,
                  border: `2px solid ${exportType === type.id ? '#667eea' : '#e8e8e8'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: exportType === type.id ? '#f0f2ff' : '#fff',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>{type.icon}</div>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{type.name}</div>
                <div style={{ color: '#999', fontSize: 12 }}>{type.count} 条</div>
              </div>
            </Col>
          ))}
        </Row>

        <div style={{ marginBottom: 24 }}>
          <span style={{ marginRight: 16 }}>时间范围：</span>
          <RangePicker 
            onChange={(dates) => setDateRange(dates)} 
            placeholder={['开始日期', '结束日期']}
          />
          <span style={{ marginLeft: 16, color: '#999' }}>（不选则导出全部数据）</span>
        </div>

        <div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={loading}
            size="large"
          >
            导出 {exportTypes.find(t => t.id === exportType)?.name}
          </Button>
          <span style={{ marginLeft: 16, color: '#999' }}>
            格式：CSV（可用Excel打开）
          </span>
        </div>
      </Card>

      {/* 导出说明 */}
      <Card title="📋 导出说明" style={{ marginTop: 16 }}>
        <ul style={{ lineHeight: 2 }}>
          <li>导出格式为CSV文件，可用Excel或WPS打开编辑</li>
          <li>建议单次导出数据量不超过10万条</li>
          <li>可选择时间范围导出指定时间段的数据</li>
          <li>导出文件会自动命名为：数据类型_日期.csv</li>
          <li>如需其他格式导出，请联系技术人员</li>
        </ul>
      </Card>

      {/* 常用导出 */}
      <Card title="⚡ 常用导出" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Button block icon={<FileExcelOutlined />} onClick={() => {
              setExportType('orders')
              setDateRange(null)
              handleExport()
            }}>
              本月订单汇总
            </Button>
          </Col>
          <Col span={8}>
            <Button block icon={<FileTextOutlined />} onClick={() => {
              setExportType('parents')
              handleExport()
            }}>
              全部家长列表
            </Button>
          </Col>
          <Col span={8}>
            <Button block icon={<FileTextOutlined />} onClick={() => {
              setExportType('learning')
              handleExport()
            }}>
              全部学习记录
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default DataExport
